import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

/*
 * The workbench's one tooltip (docs/ANALYSIS_WORKBENCH.md section 2b): the
 * name of a control whose name is not on it -- the toolbar's icon buttons,
 * the switcher's on a phone -- and the longer description a control may
 * carry (data-tip, or a title, which is moved there so the browser's own
 * tooltip never doubles it). Everything inside `rootRef` is covered; nothing
 * needs registering.
 *
 *   hover        a mouse or a hovering pen, after SHOW_DELAY; straight away
 *                while another was just shown. It stays while the pointer is
 *                on the control or on the tooltip itself (WCAG 1.4.13)
 *   keyboard     focus reached by the keyboard shows it at once; the mouse's
 *                own clicks do not
 *   touch        a long press (LONG_PRESS, the finger still) shows it without
 *                pressing the control: the click that follows is swallowed,
 *                and it stays TOUCH_LINGER after the finger lifts. A tap is
 *                an ordinary tap. A disabled control says why it is disabled
 *                this way (its title; a mouse gets the browser's own)
 *   Escape       hides it, focus staying where it is; the key goes on to
 *                what it closes as ever (a drawer, the palette), so it is
 *                never pressed twice
 *
 * A control whose name is already shown on it gets no tooltip unless it
 * carries a description. While the tooltip shows a description, the control
 * is described by it (aria-describedby); a name alone is its accessible name
 * already.
 */
export const SHOW_DELAY = 350;
export const LONG_PRESS = 500;
export const TOUCH_LINGER = 2500;
const HIDE_DELAY = 120;
// A tooltip shown this recently makes the next one show at once (moving along the toolbar).
const WARM_MS = 600;
const MOVE_SLOP = 10;
const TARGETS = 'button, a[href], select, [role="button"], [role="radio"], [role="tab"], [data-tip], [title]';

const clean = s => (s || '').replace(/\s+/g, ' ').trim();

/** The control's name: its aria-label, else what aria-labelledby names, else its text. */
function nameOf(el) {
  const label = clean(el.getAttribute('aria-label'));
  if (label) return label;
  const by = el.getAttribute('aria-labelledby');
  if (by) {
    const text = clean(by.split(/\s+/).map(id => el.ownerDocument.getElementById(id)?.textContent || '').join(' '));
    if (text) return text;
  }
  return clean(el.textContent);
}

/** What the control shows (laid-out text: a hidden label is not shown); '' where layout is unknown. */
function shownText(el) {
  return typeof el.innerText === 'string' ? clean(el.innerText) : '';
}

/**
 * A title moved to data-tip: the browser's own tooltip would double this one. A
 * disabled control keeps it: only the browser shows it to a mouse there.
 */
function adoptTitle(el) {
  const title = el.getAttribute('title');
  if (title === null || el.disabled) return;
  if (title && !el.hasAttribute('data-tip')) el.setAttribute('data-tip', title);
  el.removeAttribute('title');
}

/** What a tooltip for `el` says: {name, desc}, either null; null when it has nothing to say. */
export function tipFor(el) {
  if (!el || el.closest?.('[data-tip-off]')) return null;
  adoptTitle(el);
  const name = nameOf(el);
  const tip = clean(el.getAttribute('data-tip') || el.getAttribute('title'));
  const desc = tip && tip !== name ? tip : null;
  const shown = shownText(el);
  const nameShown = !!name && !!shown && shown.includes(name);
  const title = name && !nameShown ? name : null;
  if (!title && !desc) return null;
  return { name: title, desc };
}

export default function WorkbenchTooltip({ rootRef }) {
  const id = useId();
  const [tip, setTip] = useState(null);           // {el, name, desc}
  const bubbleRef = useRef(null);
  const tipRef = useRef(null);
  tipRef.current = tip;
  // The listeners' own hide (it also takes the description off the control), for the bubble and the effect below.
  const hideRef = useRef(() => setTip(null));

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const timers = { show: null, hide: null, press: null };
    let modality = 'pointer';
    let lastShown = 0;
    let press = null;                              // {el, x, y, pointerId, shown}
    let swallow = null;                            // {el, until}
    let described = null;                          // {el, before}
    let pendingEl = null;                          // the control a hover is waiting to show

    const clear = (name) => {
      clearTimeout(timers[name]);
      timers[name] = null;
      if (name === 'show') pendingEl = null;
    };
    const undescribe = () => {
      if (!described) return;
      const { el, before } = described;
      if (before === null) el.removeAttribute('aria-describedby');
      else el.setAttribute('aria-describedby', before);
      described = null;
    };
    const hide = () => {
      clear('show');
      clear('hide');
      undescribe();
      if (tipRef.current) lastShown = Date.now();
      setTip(null);
    };
    hideRef.current = hide;
    const show = (el) => {
      clear('show');
      clear('hide');
      const said = tipFor(el);
      if (!said || !el.isConnected) {
        hide();
        return;
      }
      undescribe();
      if (said.desc) {
        described = { el, before: el.getAttribute('aria-describedby') };
        el.setAttribute('aria-describedby', described.before ? `${described.before} ${id}` : id);
      }
      setTip({ el, ...said });
    };
    const targetOf = (node) => {
      const el = node?.closest?.(TARGETS);
      return el && root.contains(el) && !bubbleRef.current?.contains(el) ? el : null;
    };
    const warm = () => !!tipRef.current || Date.now() - lastShown < WARM_MS;

    const onOver = (e) => {
      if (e.pointerType === 'touch') return;
      if (bubbleRef.current?.contains(e.target)) {
        clear('hide');
        return;
      }
      const el = targetOf(e.target);
      // A disabled control's own title is the browser's to show a mouse: no second tooltip.
      if (!el || el.disabled) return;
      if (tipRef.current?.el === el) {
        clear('hide');
        return;
      }
      if (pendingEl === el) return;
      clear('show');
      if (warm()) {
        show(el);
        return;
      }
      pendingEl = el;
      timers.show = setTimeout(() => show(el), SHOW_DELAY);
    };
    const onOut = (e) => {
      if (e.pointerType === 'touch') return;
      const to = e.relatedTarget;
      const current = tipRef.current?.el;
      if (to && ((current && current.contains(to)) || bubbleRef.current?.contains(to))) return;
      if (pendingEl && to && pendingEl.contains(to)) return;
      clear('show');
      if (current) {
        clear('hide');
        timers.hide = setTimeout(hide, HIDE_DELAY);
      }
    };
    const onDown = (e) => {
      modality = 'pointer';
      if (e.pointerType !== 'touch') {
        if (tipRef.current && !bubbleRef.current?.contains(e.target)) hide();
        return;
      }
      clear('press');
      const el = targetOf(e.target);
      press = el ? { el, x: e.clientX, y: e.clientY, pointerId: e.pointerId, shown: false } : null;
      if (tipRef.current) hide();
      if (!press) return;
      timers.press = setTimeout(() => {
        if (!press) return;
        press.shown = true;
        show(press.el);
      }, LONG_PRESS);
    };
    const onMove = (e) => {
      if (!press || e.pointerId !== press.pointerId || press.shown) return;
      if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > MOVE_SLOP) {
        clear('press');
        press = null;
      }
    };
    const onUp = (e) => {
      if (!press || e.pointerId !== press.pointerId) return;
      clear('press');
      if (press.shown) {
        swallow = { el: press.el, until: Date.now() + 800 };
        clear('hide');
        timers.hide = setTimeout(hide, TOUCH_LINGER);
      }
      press = null;
    };
    const onClick = (e) => {
      const held = swallow;
      swallow = null;
      if (held && Date.now() <= held.until && held.el.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // A mouse click: the tooltip has said its piece.
      if (tipRef.current && !bubbleRef.current?.contains(e.target)) hide();
    };
    const onContextMenu = (e) => {
      // A long press's context menu (Android, a pen's barrel button): the tooltip instead.
      if (press?.shown || (timers.press && press)) e.preventDefault();
    };
    const onFocusIn = (e) => {
      const el = targetOf(e.target);
      if (el && modality === 'keyboard') show(el);
    };
    const onFocusOut = (e) => {
      if (tipRef.current?.el === e.target && !bubbleRef.current?.contains(e.relatedTarget)) hide();
    };
    const onKey = (e) => {
      if (e.key === 'Escape' && tipRef.current) {
        // Hidden, focus where it was; the Escape goes on to whatever it closes (a drawer, the palette),
        // so a keyboard user never presses it twice.
        hide();
        return;
      }
      if (!e.altKey && !e.ctrlKey && !e.metaKey) modality = 'keyboard';
    };
    const onScroll = () => { if (tipRef.current) hide(); };

    root.addEventListener('pointerover', onOver);
    root.addEventListener('pointerout', onOut);
    root.addEventListener('pointerdown', onDown, true);
    root.addEventListener('pointermove', onMove, true);
    root.addEventListener('pointerup', onUp, true);
    root.addEventListener('pointercancel', onUp, true);
    root.addEventListener('click', onClick, true);
    root.addEventListener('contextmenu', onContextMenu, true);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    root.addEventListener('scroll', onScroll, true);
    root.addEventListener('wheel', onScroll, { passive: true });
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onScroll);
    return () => {
      Object.keys(timers).forEach(clear);
      undescribe();
      root.removeEventListener('pointerover', onOver);
      root.removeEventListener('pointerout', onOut);
      root.removeEventListener('pointerdown', onDown, true);
      root.removeEventListener('pointermove', onMove, true);
      root.removeEventListener('pointerup', onUp, true);
      root.removeEventListener('pointercancel', onUp, true);
      root.removeEventListener('click', onClick, true);
      root.removeEventListener('contextmenu', onContextMenu, true);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('scroll', onScroll, true);
      root.removeEventListener('wheel', onScroll);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [rootRef, id]);

  // A control gone (re-rendered away) takes its tooltip with it.
  useEffect(() => {
    if (tip && !tip.el.isConnected) hideRef.current();
  });

  // Above the control, else below it; never past the screen's sides.
  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!tip || !bubble) return;
    const r = tip.el.getBoundingClientRect();
    const w = bubble.offsetWidth;
    const h = bubble.offsetHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const margin = 6;
    const above = r.top - h - margin;
    const top = above >= margin ? above : r.bottom + margin;
    const left = Math.min(Math.max(r.left + r.width / 2 - w / 2, margin), Math.max(margin, vw - margin - w));
    bubble.style.left = `${Math.round(left)}px`;
    bubble.style.top = `${Math.round(top)}px`;
    bubble.dataset.side = above >= margin ? 'above' : 'below';
    bubble.style.visibility = 'visible';
  }, [tip]);

  if (!tip) return null;
  return (
    <div ref={bubbleRef} id={id} role="tooltip" className="wb-tip" data-testid="wb-tooltip"
         style={{ visibility: 'hidden', left: 0, top: 0 }}
         onPointerLeave={(e) => {
           if (e.pointerType !== 'touch' && !tip.el.contains(e.relatedTarget)) hideRef.current();
         }}>
      {tip.name && <strong className="wb-tip-name">{tip.name}</strong>}
      {tip.desc && <span className="wb-tip-desc">{tip.desc}</span>}
    </div>
  );
}
